/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';
import { defaultMemoize } from 'reselect';
import { oc } from 'ts-optchain';

import { CalendarItem, CalendarItemStateEnum, WalkIn } from 'utils/calendarItems';
import { ElaboratedTicketGroup } from 'types';
import { SelectedInventoryItemId } from 'state/calendar';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { core, safeToNumber } from '@tock/coretypes';
import {
  deburr,
  filter,
  find,
  flatMap,
  forEach,
  groupBy,
  isEmpty,
  sortBy,
} from 'es-toolkit/compat';
import { escapeRegExp } from 'utils/regexUtils';
import { getDiner } from '@tock/shared/utils/dinerUtils';
import { getFullName } from '@tock/shared/utils/patron';
import { textFilterMatchesNameOrEmailOrPhone } from 'utils/filterUtils';
import TicketGroupStateConstants from 'constants/TicketGroupStateConstants';

export interface IPurchasesByDateTime {
  [dateTimeString: string]: core.PastPurchase[];
}

export const filterCalendarItemsForPurchases = defaultMemoize(
  (calendar: any, textFilter?: string): IPurchasesByDateTime => {
    const filteredPurchases: core.PastPurchase[] = flatMap(
      calendar as any[],
      (ticketGroups: core.ITicketGroup[]): core.PastPurchase[] => {
        const purchaseSection = flatMap(ticketGroups, (tg) => tg.purchaseDetail || []);
        let filteredParties = purchaseSection;

        if (textFilter && !isEmpty(textFilter)) {
          const deburredFilter = deburr(textFilter);
          const regex = new RegExp(escapeRegExp(deburredFilter), 'i');

          filteredParties = filter(purchaseSection, (purchase) => {
            const { dinerPatron, ownerPatron } = purchase;
            let dinerMatch = false;
            forEach(dinerPatron, (diner) => {
              const dinerMatchOptions = {
                name: getFullName(diner),
                email: diner.email,
                phone: diner.phone,
              };
              dinerMatch = textFilterMatchesNameOrEmailOrPhone(
                deburredFilter,
                regex,
                dinerMatchOptions
              );
            });

            const ownerMatchOptions = {
              name: getFullName(ownerPatron),
              email: oc(ownerPatron).email(),
              phone: oc(ownerPatron).phone(),
            };
            return !!(
              (ownerPatron &&
                textFilterMatchesNameOrEmailOrPhone(deburredFilter, regex, ownerMatchOptions)) ||
              dinerMatch
            );
          });
        }
        return sortBy(filteredParties, [
          'ticketCount',
          'ownerPatron.lastName',
        ]) as core.PastPurchase[];
      }
    ) as any[];

    return groupBy(filteredPurchases, (purchase: core.PastPurchase) => {
      return purchase.ticketDateTime;
    });
  }
);

export const getBookingBasicInfo = (calendarItem?: CalendarItem) => {
  if (calendarItem == null) {
    return {};
  }
  const itemState = calendarItem.state;

  let bookingName;
  let patron;
  switch (itemState) {
    case TicketGroupStateConstants.SOLD:
      bookingName = 'Booking';
      patron = getDiner(calendarItem.purchase);
      break;
    case TicketGroupStateConstants.HELD:
      bookingName = 'Held reservation';
      patron = oc(calendarItem).ticketSubset.heldForPatron();
      break;
    case TicketGroupStateConstants.LOCKED:
      if (oc(calendarItem).ticketSubset.purchaseVerifyInfo() != null) {
        const typeEnum = calendarItem?.ticketSubset?.purchaseVerifyInfo?.type ?? 'EMAIL';
        const type = typeEnum === 'EMAIL' ? 'email' : 'text';
        bookingName = `Sent reservation via ${type}`;
        patron = oc(calendarItem).ticketSubset.purchaseVerifyInfo.dinerPatron();
      } else {
        bookingName = 'Guest currently booking';
      }
      break;
    default:
      bookingName = 'Unsold reservation';
  }

  if (patron != null) {
    bookingName = `${bookingName} for ${getFullName(patron)}`;
  }

  return {
    patron: patron || null,
    bookingName: bookingName || null,
    purchaseDetails: calendarItem.purchase || null,
    ticketGroup: calendarItem.ticketGroup || null,
  };
};

export const getReschedulingErrors = (
  purchase: core.PastPurchase,
  source: core.TicketGroup,
  destination: core.TicketGroup,
  destinationDateTime: LocalDateTime
) => {
  const conditions: string[] = [];
  if (destinationDateTime.isBefore(LocalDateTime.now())) {
    conditions.push('The selected table is in the past.');
  }

  const destinationTypes = destination.type.map((price: core.IPrice) => {
    return safeToNumber(price.id);
  });

  if (destinationTypes.indexOf(safeToNumber(purchase.ticketTypeId)) < 0) {
    let condition = "The selected table does not normally offer the original table's experience.";
    if (destination.serviceType !== source.serviceType) {
      condition += " Make sure the reservation has the correct experience once you've rescheduled.";
    }
    conditions.push(condition);
  }

  if (destination.maxSize < purchase.ticketCount && !destination.finishedByTime) {
    conditions.push('The selected slot is too small for the reservation.');
  } else if (
    destination.maxSize !== source.maxSize &&
    destination.tableVariety !== 'COMMUNAL_TABLE' &&
    source.tableVariety !== 'COMMUNAL_TABLE' &&
    !destination.finishedByTime
  ) {
    conditions.push('The selected table is not the same size as the original table.');
  }

  if (conditions.length === 0) {
    return '';
  }
  let warningsMessage;
  if (conditions.length === 1) {
    warningsMessage = conditions[0];
  } else {
    warningsMessage = 'We have detected the following issues:\n' + conditions.join('\n');
  }

  return warningsMessage;
};

export const getSelectedInventoryItemId = (
  calendarItem?: CalendarItem
): SelectedInventoryItemId | undefined => {
  if (!calendarItem) {
    return undefined;
  }

  if (isWalkInCalendarItem(calendarItem)) {
    return {
      type: 'WALK_IN',
      partyId: calendarItem.party.id,
    };
  }

  switch (calendarItem.state) {
    case CalendarItemStateEnum.AVAILABLE:
      return {
        type: CalendarItemStateEnum.AVAILABLE,
        calendarItemId: calendarItem.id,
      };
    case CalendarItemStateEnum.COMMUNAL:
      return {
        type: CalendarItemStateEnum.COMMUNAL,
        calendarItemId: calendarItem.id,
      };
    case CalendarItemStateEnum.HELD:
      return {
        type: CalendarItemStateEnum.HELD,
        ticketSubsetId: calendarItem.ticketSubset.id,
      };
    case CalendarItemStateEnum.LOCKED:
      return {
        type: CalendarItemStateEnum.LOCKED,
        ticketSubsetId: calendarItem.ticketSubset.id,
      };
    case CalendarItemStateEnum.SOLD:
      return {
        type: CalendarItemStateEnum.SOLD,
        originalPurchaseId: calendarItem.purchase.originalPurchaseId,
      };
    case CalendarItemStateEnum.TIME_FLEXIBLE:
      return {
        type: CalendarItemStateEnum.TIME_FLEXIBLE,
        calendarItemId: calendarItem.id,
      };
    default:
      return undefined;
  }
};

export const getCalendarItemFromTicketGroupsByOption = (
  ticketGroups: ElaboratedTicketGroup[],
  option?: SelectedInventoryItemId
): CalendarItem | undefined => {
  if (!option) {
    return undefined;
  }

  let selectedCalendarItem: CalendarItem | undefined;
  forEach(ticketGroups, (ticketGroup: ElaboratedTicketGroup) => {
    selectedCalendarItem = getCalendarItemFromListByOption(ticketGroup.calendarItems, option);
    if (selectedCalendarItem) {
      return false;
    } else {
      return undefined;
    }
  });
  return selectedCalendarItem;
};

export const getCalendarItemFromListByOption = (
  calendarItems: CalendarItem[],
  option: SelectedInventoryItemId
) => {
  switch (option.type) {
    case 'WALK_IN':
      return find(calendarItems, (calendarItem: CalendarItem) => {
        if (isWalkInCalendarItem(calendarItem)) {
          return calendarItem.party.id === option.partyId;
        } else {
          return false;
        }
      });

    case CalendarItemStateEnum.TIME_FLEXIBLE:
    case CalendarItemStateEnum.COMMUNAL:
    case CalendarItemStateEnum.AVAILABLE:
      return find(calendarItems, (calendarItem) => calendarItem.id === option.calendarItemId);
    case CalendarItemStateEnum.LOCKED:
    case CalendarItemStateEnum.HELD:
      return find(
        calendarItems,
        (calendarItem: CalendarItem) =>
          calendarItem.ticketSubset && calendarItem.ticketSubset.id === option.ticketSubsetId
      );

    case CalendarItemStateEnum.SOLD:
      return find(
        calendarItems,
        (calendarItem) =>
          calendarItem.purchase &&
          calendarItem.purchase.originalPurchaseId === option.originalPurchaseId
      );

    default:
      assertNever(option);
      return undefined;
  }
};

export const isWalkInCalendarItem = (calendarItem: CalendarItem): calendarItem is WalkIn => {
  return calendarItem.state === 'SOLD' && !calendarItem.purchase;
};
